import React, { useEffect, useState } from 'react';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { getDocs, query, where } from 'firebase/firestore';
import { useFirebaseService } from '../../../data/FirebaseService';
import { Typography } from '@mui/material';
import InviteUsers from './InviteUsers';
import { allRoles, useAuthService } from '../../login/authentication';
import { Delete, Email } from '@mui/icons-material';
import ConfirmationDialog from '../../../ui/ConfirmationDialog';
import { toast } from 'react-toastify';
import { httpsCallable } from 'firebase/functions';
import EditUserDialog from './EditUserDialog';

export interface User {
    uid: string;
    name: string;
    email: string;
    status: UserStatus;
    roles: string[];
}

export enum UserStatus {
    Pending = 'pending',
    Active = 'active',
    Deleted = 'deleted'
}

const userStatusTitles: { [key in UserStatus]: string } = {
    [UserStatus.Pending]: 'Invited',
    [UserStatus.Active]: 'Active',
    [UserStatus.Deleted]: 'Deleted'
};

const UsersPage: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [invitations, setInvitations] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openEditUserDialog, setOpenEditUserDialog] = useState(false);
    const [currentRow, setCurrentRow] = useState<User | null>(null);

    const authService = useAuthService();
    const firebaseService = useFirebaseService();

    useEffect(() => {
        if (firebaseService) {
            const fetchUsers = async () => {
                const usersCollection = firebaseService.getCollection('users');
                const usersSnapshot = await getDocs(usersCollection);
                const usersList = usersSnapshot.docs.map(doc => ({
                    uid: doc.id,
                    ...doc.data()
                })) as User[];

                const invitesCollection = firebaseService.getCollection('invitations');
                const invitesQuery = await query(invitesCollection, where('status', '==', UserStatus.Pending));
                const invitesSnapshot = await getDocs(invitesQuery);
                const invitesList = invitesSnapshot.docs.map(doc => ({
                    uid: doc.id,
                    roles: [doc.data().role],
                    ...doc.data()
                })) as User[];
                setInvitations(invitesList);
                setUsers([...usersList, ...invitesList]);
                setLoading(false);
            };


            fetchUsers();
        }
    }, [firebaseService]);

    const columns: GridColDef[] = [
        { field: 'name', headerName: 'Name', width: 200 },
        { field: 'email', headerName: 'Email', width: 250 },
        {
            field: 'roles',
            headerName: 'Roles',
            width: 300,
            renderCell: (params) => (
                <div>
                    {params.value.map((role: string, index: number) => (
                        <span key={index}>{allRoles[role as keyof typeof allRoles]}{index < params.value.length - 1 ? ', ' : ''}</span>
                    ))}
                </div>
            )
        },
        { field: 'status', headerName: 'Status', width: 100, valueGetter: (params, row) => userStatusTitles[(row as User).status] },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 150,
            align: 'right',
            getActions: (params) => {
                const actions = [];
                if (params.row.status === UserStatus.Pending) {
                    actions.push(
                        <GridActionsCellItem
                            label="Resend Invite"
                            title="Resend Invite"
                            onClick={() => handleResendInvite(params.row)}
                            showInMenu={false}
                            icon={<Email />}
                        />
                    );
                }
                // Don't allow users to delete themselves

                const currentUserEmail = authService.getCurrentUserName();

                if (params.row.email !== currentUserEmail) {
                    actions.push(
                        <GridActionsCellItem
                            label="Delete User"
                            title="Delete User"
                            onClick={() => handleDelete(params.row)}
                            showInMenu={false}
                            icon={<Delete />}
                        />
                    );
                }
                return actions;
            }
        }
    ];

    const handleResendInvite = async (row: User) => {
        if (firebaseService) {
            try {
                const resendInvite = httpsCallable(firebaseService.functions, "resendInvitation");
                await resendInvite({ invitationId: row.uid });
                toast.success('Invite resent successfully.', { position: 'bottom-right' });
            } catch (e) {
                console.error(e);
                toast.error('Failed to resend invite. Please try again.', { position: 'bottom-right' });
            }
        }
    };

    const handleDelete = (row: User) => {
        setCurrentRow(row);
        setOpenDeleteDialog(true);
    }

    const handleConfirmDelete = async () => {
        if (firebaseService && currentRow) {
            try {
                const deleteUser = httpsCallable(firebaseService.functions, "deleteUser");
                const data = (currentRow.status === 'pending') ? { inviteId: currentRow.uid } : { uid: currentRow.uid };
                await deleteUser(data);
                setUsers(users.filter(user => user.uid !== currentRow.uid));
            }
            catch (e) {
                console.error(e);
                toast.error('Failed to delete user. Please try again.', { position: 'bottom-right' });
            }
        }
        // Add your disable logic here
        setOpenDeleteDialog(false); // Close the dialog after confirming
    };

    function handleUserInvited(user: User): void {
        setInvitations([...invitations, user]);
    }

    function handleRowClick(params: any): void {
        setCurrentRow(params.row as User);
        setOpenEditUserDialog(true);
    }


    return (
        <div>
            <Typography variant="h5" component="h2" className='page-title'>Users</Typography>
            <InviteUsers onUserInvited={handleUserInvited} />
            <div style={{ marginTop: '20px', height: 'calc(100vh - 220px)', width: '100%' }}>
                <DataGrid
                    rows={users}
                    columns={columns}
                    getRowId={(row) => row.uid}
                    loading={loading}
                    onRowClick={handleRowClick} />
            </div>

            <ConfirmationDialog open={openDeleteDialog}
                title='Confirm Delete'
                content='Are you sure you want to delete this user?'
                onConfirm={handleConfirmDelete}
                onCancel={() => setOpenDeleteDialog(false)} />


            <EditUserDialog open={openEditUserDialog}
                user={currentRow}
                onSave={(user: User) => {
                    setUsers(users.map(u => u.uid === user.uid ? user : u));
                }}
                onClose={() => setOpenEditUserDialog(false)} />
        </div>

    );
};

export default UsersPage;